/**
 * Well Assignment Types & Interfaces
 * 
 * Part of the Well Assignment Feature v2.1
 * Reference: WELL_ASSIGNMENT_FEATURE.md
 * 
 * @module wellAssignment
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Method by which a well was assigned to a pumper.
 * Used for audit trail and assignment history tracking.
 * 
 * - manual: Single well assigned via UI dialog
 * - bulk: Multiple wells assigned at once via multi-select
 * - route: Wells assigned via route-based assignment
 * - auto: Automatically assigned when pumper creates a well
 * - coverage: Assigned as part of temporary coverage arrangement
 * - backup_activation: Backup pumper activated to cover primary
 */
export type AssignmentMethod = 
  | 'manual' 
  | 'bulk' 
  | 'route' 
  | 'auto' 
  | 'coverage' 
  | 'backup_activation';

/**
 * Enum values for AssignmentMethod (for iteration/validation)
 */
export const ASSIGNMENT_METHODS: readonly AssignmentMethod[] = [
  'manual',
  'bulk',
  'route',
  'auto',
  'coverage',
  'backup_activation',
] as const;

/**
 * Human-readable labels for assignment methods
 */
export const ASSIGNMENT_METHOD_LABELS: Record<AssignmentMethod, string> = {
  manual: 'Manual Assignment',
  bulk: 'Bulk Assignment',
  route: 'Route-Based Assignment',
  auto: 'Auto-Assigned on Creation',
  coverage: 'Temporary Coverage',
  backup_activation: 'Backup Activation',
};

/**
 * Reason for temporary coverage assignment.
 * Used when one pumper temporarily covers another's wells.
 * 
 * - on_vacation: Planned vacation time off
 * - sick: Illness (short-term)
 * - injury: Injury leave (may be extended)
 * - training: Attending training sessions
 * - helping_crew: Temporarily helping another crew
 * - other: Any other reason (requires reasonNote)
 */
export type CoverageReason = 
  | 'on_vacation' 
  | 'sick' 
  | 'injury' 
  | 'training' 
  | 'helping_crew' 
  | 'other';

/**
 * Enum values for CoverageReason (for iteration/validation)
 */
export const COVERAGE_REASONS: readonly CoverageReason[] = [
  'on_vacation',
  'sick',
  'injury',
  'training',
  'helping_crew',
  'other',
] as const;

/**
 * Human-readable labels for coverage reasons
 */
export const COVERAGE_REASON_LABELS: Record<CoverageReason, string> = {
  on_vacation: 'On Vacation',
  sick: 'Sick',
  injury: 'Injury',
  training: 'Training',
  helping_crew: 'Helping Another Crew',
  other: 'Other',
};

/**
 * Whether this coverage reason typically has a known end date
 * Used to suggest end date vs reminder-based coverage in UI
 */
export const COVERAGE_REASON_HAS_END_DATE: Record<CoverageReason, boolean> = {
  on_vacation: true,     // Vacations usually have return dates
  sick: false,           // Sickness duration often unknown
  injury: false,         // Injury recovery time uncertain
  training: true,        // Training has scheduled end
  helping_crew: true,    // Usually planned duration
  other: false,          // Unknown by default
};

/**
 * Action to take when temporary coverage ends.
 * Configured when creating the coverage, executed by scheduled Cloud Function.
 * 
 * - reassign_original: Automatically reassign wells back to original pumper
 * - unassign: Remove assignment entirely (wells become orphaned/unassigned)
 * - notify_only: Send notification to manager, take no automatic action
 */
export type CoverageEndAction = 
  | 'reassign_original' 
  | 'unassign' 
  | 'notify_only';

/**
 * Enum values for CoverageEndAction (for iteration/validation)
 */
export const COVERAGE_END_ACTIONS: readonly CoverageEndAction[] = [
  'reassign_original',
  'unassign',
  'notify_only',
] as const;

/**
 * Human-readable labels for coverage end actions
 */
export const COVERAGE_END_ACTION_LABELS: Record<CoverageEndAction, string> = {
  reassign_original: 'Reassign to Original Pumper',
  unassign: 'Leave Unassigned',
  notify_only: 'Notify Manager Only',
};

/**
 * Descriptions for coverage end actions (for UI tooltips/help text)
 */
export const COVERAGE_END_ACTION_DESCRIPTIONS: Record<CoverageEndAction, string> = {
  reassign_original: 'Wells will automatically be reassigned back to the original pumper when coverage ends',
  unassign: 'Wells will be left unassigned and appear in the orphan wells list for manual assignment',
  notify_only: 'Manager will be notified when coverage ends but no automatic changes will be made',
};

/**
 * Status of a temporary coverage assignment.
 * Tracks the lifecycle of coverage from creation to completion.
 * 
 * - active: Coverage is currently in effect
 * - ended: Coverage completed (scheduled end, early return, or made permanent)
 * - extended: Coverage was extended beyond original end date (via "Extend 7 days" action)
 */
export type TemporaryAssignmentStatus = 
  | 'active' 
  | 'ended' 
  | 'extended';

/**
 * Enum values for TemporaryAssignmentStatus (for iteration/validation)
 */
export const TEMPORARY_ASSIGNMENT_STATUSES: readonly TemporaryAssignmentStatus[] = [
  'active',
  'ended',
  'extended',
] as const;

/**
 * Human-readable labels for temporary assignment statuses
 */
export const TEMPORARY_ASSIGNMENT_STATUS_LABELS: Record<TemporaryAssignmentStatus, string> = {
  active: 'Active',
  ended: 'Ended',
  extended: 'Extended',
};

/**
 * Badge/chip colors for temporary assignment statuses (Tailwind classes)
 * Used in TemporaryAssignmentPanel.tsx and coverage list displays
 */
export const TEMPORARY_ASSIGNMENT_STATUS_COLORS: Record<TemporaryAssignmentStatus, {
  bg: string;
  text: string;
  border: string;
}> = {
  active: {
    bg: 'bg-green-500/10',
    text: 'text-green-500',
    border: 'border-green-500/20',
  },
  ended: {
    bg: 'bg-gray-500/10',
    text: 'text-gray-500',
    border: 'border-gray-500/20',
  },
  extended: {
    bg: 'bg-blue-500/10',
    text: 'text-blue-500',
    border: 'border-blue-500/20',
  },
};

/**
 * Reason why a temporary coverage assignment ended.
 * Recorded when coverage terminates for audit trail and reporting.
 * 
 * - completed: Coverage ended on its scheduled end date (normal termination)
 * - early_return: Original pumper returned before scheduled end date
 * - made_permanent: Manager converted temporary assignment to permanent
 * - manual: Manager manually ended coverage for other reasons
 */
export type CoverageEndReason = 
  | 'completed' 
  | 'early_return' 
  | 'made_permanent' 
  | 'manual';

/**
 * Enum values for CoverageEndReason (for iteration/validation)
 */
export const COVERAGE_END_REASONS: readonly CoverageEndReason[] = [
  'completed',
  'early_return',
  'made_permanent',
  'manual',
] as const;

/**
 * Human-readable labels for coverage end reasons
 */
export const COVERAGE_END_REASON_LABELS: Record<CoverageEndReason, string> = {
  completed: 'Scheduled End',
  early_return: 'Early Return',
  made_permanent: 'Made Permanent',
  manual: 'Manually Ended',
};

/**
 * Badge/chip colors for coverage end reasons (Tailwind classes)
 * Used in WellAssignmentHistory.tsx and coverage detail views
 */
export const COVERAGE_END_REASON_COLORS: Record<CoverageEndReason, {
  bg: string;
  text: string;
  border: string;
}> = {
  completed: {
    bg: 'bg-green-500/10',
    text: 'text-green-500',
    border: 'border-green-500/20',
  },
  early_return: {
    bg: 'bg-amber-500/10',
    text: 'text-amber-500',
    border: 'border-amber-500/20',
  },
  made_permanent: {
    bg: 'bg-blue-500/10',
    text: 'text-blue-500',
    border: 'border-blue-500/20',
  },
  manual: {
    bg: 'bg-gray-500/10',
    text: 'text-gray-500',
    border: 'border-gray-500/20',
  },
};

/**
 * Action type recorded in assignment history.
 * Tracks what happened to a well's assignment for audit trail.
 * 
 * - assigned: Pumper was assigned to the well (primary or added to multi-pumper)
 * - unassigned: Pumper was removed from the well
 * - backup_activated: Backup pumper took over for primary (emergency/coverage)
 * - backup_deactivated: Backup pumper returned control to primary
 * - temporary_started: Temporary coverage began
 * - temporary_ended: Temporary coverage ended
 * - temporary_extended: Temporary coverage was extended
 */
export type AssignmentAction = 
  | 'assigned' 
  | 'unassigned' 
  | 'backup_activated' 
  | 'backup_deactivated'
  | 'temporary_started'
  | 'temporary_ended'
  | 'temporary_extended';

/**
 * Enum values for AssignmentAction (for iteration/validation)
 */
export const ASSIGNMENT_ACTIONS: readonly AssignmentAction[] = [
  'assigned',
  'unassigned',
  'backup_activated',
  'backup_deactivated',
  'temporary_started',
  'temporary_ended',
  'temporary_extended',
] as const;

/**
 * Human-readable labels for assignment actions
 */
export const ASSIGNMENT_ACTION_LABELS: Record<AssignmentAction, string> = {
  assigned: 'Assigned',
  unassigned: 'Unassigned',
  backup_activated: 'Backup Activated',
  backup_deactivated: 'Backup Deactivated',
  temporary_started: 'Coverage Started',
  temporary_ended: 'Coverage Ended',
  temporary_extended: 'Coverage Extended',
};

/**
 * Past-tense labels for assignment actions (for history timeline display)
 * e.g., "John Smith was assigned to Alpha-5"
 */
export const ASSIGNMENT_ACTION_PAST_LABELS: Record<AssignmentAction, string> = {
  assigned: 'was assigned to',
  unassigned: 'was unassigned from',
  backup_activated: 'activated as backup for',
  backup_deactivated: 'deactivated as backup for',
  temporary_started: 'started covering',
  temporary_ended: 'ended coverage for',
  temporary_extended: 'extended coverage for',
};

/**
 * Badge/chip colors for assignment actions (Tailwind classes)
 * Used in WellAssignmentHistory.tsx timeline
 */
export const ASSIGNMENT_ACTION_COLORS: Record<AssignmentAction, {
  bg: string;
  text: string;
  border: string;
  icon: string;  // Suggested lucide-react icon name
}> = {
  assigned: {
    bg: 'bg-green-500/10',
    text: 'text-green-500',
    border: 'border-green-500/20',
    icon: 'UserPlus',
  },
  unassigned: {
    bg: 'bg-red-500/10',
    text: 'text-red-500',
    border: 'border-red-500/20',
    icon: 'UserMinus',
  },
  backup_activated: {
    bg: 'bg-amber-500/10',
    text: 'text-amber-500',
    border: 'border-amber-500/20',
    icon: 'ShieldCheck',
  },
  backup_deactivated: {
    bg: 'bg-gray-500/10',
    text: 'text-gray-500',
    border: 'border-gray-500/20',
    icon: 'ShieldOff',
  },
  temporary_started: {
    bg: 'bg-blue-500/10',
    text: 'text-blue-500',
    border: 'border-blue-500/20',
    icon: 'Clock',
  },
  temporary_ended: {
    bg: 'bg-purple-500/10',
    text: 'text-purple-500',
    border: 'border-purple-500/20',
    icon: 'ClockArrowDown',
  },
  temporary_extended: {
    bg: 'bg-cyan-500/10',
    text: 'text-cyan-500',
    border: 'border-cyan-500/20',
    icon: 'ClockArrowUp',
  },
};

/**
 * Workload status for a pumper based on calculated workload score.
 * Used for visual indicators and assignment recommendations.
 * 
 * Score thresholds (from WELL_ASSIGNMENT_FEATURE.md):
 * - light: score < 30 — pumper can take more wells
 * - balanced: score 30-60 — optimal workload
 * - heavy: score 60-80 — nearing capacity
 * - overloaded: score > 80 — needs wells redistributed
 */
export type WorkloadStatus = 
  | 'light' 
  | 'balanced' 
  | 'heavy' 
  | 'overloaded';

/**
 * Enum values for WorkloadStatus (for iteration/validation)
 */
export const WORKLOAD_STATUSES: readonly WorkloadStatus[] = [
  'light',
  'balanced',
  'heavy',
  'overloaded',
] as const;

/**
 * Score thresholds for workload status calculation
 * Used by getWorkloadStatus() in wellAssignmentService.ts
 */
export const WORKLOAD_THRESHOLDS: Record<WorkloadStatus, { min: number; max: number }> = {
  light: { min: 0, max: 29 },
  balanced: { min: 30, max: 60 },
  heavy: { min: 61, max: 80 },
  overloaded: { min: 81, max: 100 },
};

/**
 * Human-readable labels for workload statuses
 */
export const WORKLOAD_STATUS_LABELS: Record<WorkloadStatus, string> = {
  light: 'Light',
  balanced: 'Balanced',
  heavy: 'Heavy',
  overloaded: 'Overloaded',
};

/**
 * Labels with emoji indicators for workload statuses
 * Used in pumper dropdowns and list views for quick visual scanning
 */
export const WORKLOAD_STATUS_EMOJI_LABELS: Record<WorkloadStatus, string> = {
  light: '🟢 Light',
  balanced: '🟡 Balanced',
  heavy: '🟠 Heavy',
  overloaded: '🔴 Overloaded',
};

/**
 * Descriptions for workload statuses (for tooltips/help text)
 */
export const WORKLOAD_STATUS_DESCRIPTIONS: Record<WorkloadStatus, string> = {
  light: 'This pumper has capacity for additional wells',
  balanced: 'This pumper has an optimal workload',
  heavy: 'This pumper is nearing capacity — assign with caution',
  overloaded: 'This pumper is overloaded — consider redistributing wells',
};

/**
 * Badge/chip colors for workload statuses (Tailwind classes)
 * Used in WorkloadDashboardPanel.tsx and pumper selection dropdowns
 */
export const WORKLOAD_STATUS_COLORS: Record<WorkloadStatus, {
  bg: string;
  text: string;
  border: string;
  dot: string;      // Solid color for dot indicator
  icon: string;     // Suggested lucide-react icon name
}> = {
  light: {
    bg: 'bg-green-500/10',
    text: 'text-green-500',
    border: 'border-green-500/20',
    dot: 'bg-green-500',
    icon: 'CircleDot',
  },
  balanced: {
    bg: 'bg-yellow-500/10',
    text: 'text-yellow-500',
    border: 'border-yellow-500/20',
    dot: 'bg-yellow-500',
    icon: 'Circle',
  },
  heavy: {
    bg: 'bg-orange-500/10',
    text: 'text-orange-500',
    border: 'border-orange-500/20',
    dot: 'bg-orange-500',
    icon: 'AlertCircle',
  },
  overloaded: {
    bg: 'bg-red-500/10',
    text: 'text-red-500',
    border: 'border-red-500/20',
    dot: 'bg-red-500',
    icon: 'XCircle',
  },
};

/**
 * Assignment recommendation based on workload status
 * Guides managers when selecting pumpers in assignment dialogs
 */
export const WORKLOAD_STATUS_RECOMMENDATIONS: Record<WorkloadStatus, {
  canAssign: boolean;
  warning: string | null;
  suggestion: string;
}> = {
  light: {
    canAssign: true,
    warning: null,
    suggestion: 'Good candidate for additional wells',
  },
  balanced: {
    canAssign: true,
    warning: null,
    suggestion: 'Optimal workload — proceed normally',
  },
  heavy: {
    canAssign: true,
    warning: 'This pumper is nearing capacity',
    suggestion: 'Consider assigning to a lighter-loaded pumper instead',
  },
  overloaded: {
    canAssign: true,  // Still allowed, but with strong warning
    warning: '⚠️ This pumper is overloaded!',
    suggestion: 'Strongly recommend redistributing wells before adding more',
  },
};

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Temporary assignment data embedded in a Well document.
 * Tracks when a well is being covered by someone other than the primary pumper.
 * 
 * This is embedded directly in the well document for fast reads.
 * A separate TemporaryAssignmentDocument in the temporary_assignments collection
 * provides the master record for managing coverage across multiple wells.
 */
export interface TemporaryAssignment {
  /** Whether this temporary assignment is currently active */
  active: boolean;
  
  /** Pumper providing coverage */
  coveringPumperId: string;
  coveringPumperName: string;
  
  /** Pumper being covered (original assignee) */
  originalPumperId: string;
  originalPumperName: string;
  
  /** Reason for the coverage */
  reason: CoverageReason;
  /** Optional note with additional details (required when reason is 'other') */
  reasonNote?: string;
  
  /** When coverage started */
  startDate: Date | { seconds: number; nanoseconds: number };  // Firestore Timestamp
  
  /** Known end date (null if using reminder-based coverage) */
  endDate: Date | { seconds: number; nanoseconds: number } | null;
  
  /** Reminder date for uncertain-duration coverage (null if using fixed end date) */
  reminderDate: Date | { seconds: number; nanoseconds: number } | null;
  
  /** What to do when coverage ends */
  endAction: 'reassign' | 'unassign' | 'notify';
  
  /** Who created this coverage arrangement */
  createdBy: string;
  createdByName: string;
  createdAt: Date | { seconds: number; nanoseconds: number };  // Firestore Timestamp
}

/**
 * Type guard to check if a well has active temporary coverage
 */
export function hasActiveTemporaryAssignment(
  temporaryAssignment: TemporaryAssignment | undefined | null
): temporaryAssignment is TemporaryAssignment {
  return temporaryAssignment?.active === true;
}

/**
 * Default empty state for temporary assignment (for initialization)
 */
export const EMPTY_TEMPORARY_ASSIGNMENT: Partial<TemporaryAssignment> = {
  active: false,
};

/**
 * Firestore Timestamp type for flexibility with raw data vs converted dates
 */
export type FirestoreTimestamp = Date | { seconds: number; nanoseconds: number };

/**
 * Standalone document in the temporary_assignments collection.
 * Master record for managing coverage across multiple wells.
 * 
 * Path: companies/{companyId}/temporary_assignments/{assignmentId}
 * 
 * This is the source of truth for coverage arrangements.
 * Each well also has an embedded TemporaryAssignment for fast reads,
 * but this document tracks the full coverage lifecycle and all affected wells.
 */
export interface TemporaryAssignmentDocument {
  /** Document ID (matches Firestore doc ID) */
  id: string;
  
  /** Company ID for tenant isolation — REQUIRED */
  companyId: string;
  
  // ─────────────────────────────────────────────────────────────────────────
  // PUMPER INFORMATION
  // ─────────────────────────────────────────────────────────────────────────
  
  /** Pumper providing coverage */
  coveringPumperId: string;
  coveringPumperName: string;
  
  /** Pumper being covered (original assignee whose wells are being covered) */
  originalPumperId: string;
  originalPumperName: string;
  
  // ─────────────────────────────────────────────────────────────────────────
  // WELLS COVERED
  // ─────────────────────────────────────────────────────────────────────────
  
  /** IDs of all wells included in this coverage */
  wellIds: string[];
  
  /** Names of wells for display without extra queries */
  wellNames: string[];
  
  /** Count of wells (denormalized for quick display) */
  wellCount: number;
  
  // ─────────────────────────────────────────────────────────────────────────
  // COVERAGE DETAILS
  // ─────────────────────────────────────────────────────────────────────────
  
  /** Reason for the coverage */
  reason: CoverageReason;
  
  /** Optional note with additional details */
  reasonNote?: string;
  
  /** When coverage started */
  startDate: FirestoreTimestamp;
  
  /** 
   * Known end date (null if using reminder-based coverage)
   * Mutually exclusive with reminderDate — one should always be set
   */
  endDate: FirestoreTimestamp | null;
  
  /** 
   * Reminder date for uncertain-duration coverage (null if using fixed end date)
   * When reached, manager is notified to check if coverage should continue
   */
  reminderDate: FirestoreTimestamp | null;
  
  /** Whether the reminder notification has been sent */
  reminderSent: boolean;
  
  /** What to do when coverage ends */
  endAction: 'reassign' | 'unassign' | 'notify';
  
  // ─────────────────────────────────────────────────────────────────────────
  // STATUS & LIFECYCLE
  // ─────────────────────────────────────────────────────────────────────────
  
  /** Current status of this coverage */
  status: TemporaryAssignmentStatus;
  
  /** When coverage was ended (only set when status is 'ended') */
  endedAt?: FirestoreTimestamp;
  
  /** Who ended the coverage */
  endedBy?: string;
  endedByName?: string;
  
  /** Why coverage ended */
  endReason?: CoverageEndReason;
  
  // ─────────────────────────────────────────────────────────────────────────
  // AUDIT FIELDS
  // ─────────────────────────────────────────────────────────────────────────
  
  /** Who created this coverage — REQUIRED */
  createdBy: string;
  createdByName: string;
  
  /** When created — REQUIRED */
  createdAt: FirestoreTimestamp;
  
  /** Last update timestamp */
  updatedAt?: FirestoreTimestamp;
}

// ─────────────────────────────────────────────────────────────────────────────
// TEMPORARY ASSIGNMENT HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Type guard to check if a coverage document is currently active
 */
export function isActiveCoverage(
  doc: TemporaryAssignmentDocument | undefined | null
): doc is TemporaryAssignmentDocument {
  return doc?.status === 'active' || doc?.status === 'extended';
}

/**
 * Type guard to check if a coverage document has ended
 */
export function isEndedCoverage(
  doc: TemporaryAssignmentDocument | undefined | null
): doc is TemporaryAssignmentDocument {
  return doc?.status === 'ended';
}

/**
 * Check if coverage is using reminder-based (unknown duration) mode
 */
export function isReminderBasedCoverage(doc: TemporaryAssignmentDocument): boolean {
  return doc.endDate === null && doc.reminderDate !== null;
}

/**
 * Check if coverage is using fixed end date mode
 */
export function isFixedEndDateCoverage(doc: TemporaryAssignmentDocument): boolean {
  return doc.endDate !== null && doc.reminderDate === null;
}

/**
 * Check if a reminder is due (reminder date has passed and not yet sent)
 */
export function isReminderDue(doc: TemporaryAssignmentDocument, now: Date = new Date()): boolean {
  if (!doc.reminderDate || doc.reminderSent) return false;
  
  const reminderDate = doc.reminderDate instanceof Date 
    ? doc.reminderDate 
    : new Date(doc.reminderDate.seconds * 1000);
  
  return reminderDate <= now;
}

/**
 * Check if coverage is ending soon (within specified days)
 */
export function isCoverageEndingSoon(
  doc: TemporaryAssignmentDocument, 
  withinDays: number = 1,
  now: Date = new Date()
): boolean {
  if (!doc.endDate || doc.status !== 'active') return false;
  
  const endDate = doc.endDate instanceof Date 
    ? doc.endDate 
    : new Date(doc.endDate.seconds * 1000);
  
  const daysUntilEnd = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return daysUntilEnd >= 0 && daysUntilEnd <= withinDays;
}

/**
 * Get days remaining until coverage ends (negative if past due)
 */
export function getDaysUntilCoverageEnds(
  doc: TemporaryAssignmentDocument,
  now: Date = new Date()
): number | null {
  if (!doc.endDate) return null;
  
  const endDate = doc.endDate instanceof Date 
    ? doc.endDate 
    : new Date(doc.endDate.seconds * 1000);
  
  return Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Default values for creating a new TemporaryAssignmentDocument
 */
export const DEFAULT_TEMPORARY_ASSIGNMENT_DOCUMENT: Pick<
  TemporaryAssignmentDocument,
  'status' | 'reminderSent' | 'wellIds' | 'wellNames' | 'wellCount'
> = {
  status: 'active',
  reminderSent: false,
  wellIds: [],
  wellNames: [],
  wellCount: 0,
};

/**
 * Fields required when creating a new TemporaryAssignmentDocument
 */
export type CreateTemporaryAssignmentInput = Omit<
  TemporaryAssignmentDocument,
  'id' | 'status' | 'reminderSent' | 'endedAt' | 'endedBy' | 'endedByName' | 'endReason' | 'updatedAt'
>;

/**
 * Fields that can be updated on an existing TemporaryAssignmentDocument
 */
export type UpdateTemporaryAssignmentInput = Partial<
  Pick<
    TemporaryAssignmentDocument,
    'endDate' | 'reminderDate' | 'reminderSent' | 'status' | 'endedAt' | 'endedBy' | 'endedByName' | 'endReason' | 'updatedAt'
  >
>;

/**
 * Method used for assignment history tracking.
 * Extended version of AssignmentMethod with additional history-specific values.
 * 
 * - manual: Single well assigned via UI dialog
 * - route: Wells assigned via route-based assignment
 * - auto: Automatically assigned when pumper creates a well
 * - bulk: Multiple wells assigned at once via multi-select
 * - temporary: Assigned as part of temporary coverage
 * - coverage_end: Assignment changed when coverage ended
 * - backup: Backup pumper activation/deactivation
 */
export type HistoryMethod = 
  | 'manual' 
  | 'route' 
  | 'auto' 
  | 'bulk' 
  | 'temporary' 
  | 'coverage_end' 
  | 'backup';

/**
 * Enum values for HistoryMethod (for iteration/validation)
 */
export const HISTORY_METHODS: readonly HistoryMethod[] = [
  'manual',
  'route',
  'auto',
  'bulk',
  'temporary',
  'coverage_end',
  'backup',
] as const;

/**
 * Human-readable labels for history methods
 */
export const HISTORY_METHOD_LABELS: Record<HistoryMethod, string> = {
  manual: 'Manual',
  route: 'Route Assignment',
  auto: 'Auto-Assigned',
  bulk: 'Bulk Assignment',
  temporary: 'Temporary Coverage',
  coverage_end: 'Coverage Ended',
  backup: 'Backup System',
};

/**
 * Metadata embedded in assignment history entries.
 * Provides additional context for temporary/coverage-related changes.
 */
export interface AssignmentHistoryMetadata {
  /** Whether this was part of a temporary assignment */
  temporaryAssignment?: boolean;
  
  /** ID of pumper being covered (for coverage-related entries) */
  coveringFor?: string;
  
  /** Name of pumper being covered */
  coveringForName?: string;
  
  /** Reason for coverage (if temporary assignment) */
  coverageReason?: CoverageReason;
  
  /** End date of coverage (if temporary assignment) */
  endDate?: FirestoreTimestamp;
}

/**
 * Entry in the assignment history subcollection.
 * Records every assignment change for audit trail and timeline display.
 * 
 * Path: companies/{companyId}/wells/{wellId}/assignment_history/{historyId}
 * 
 * Each entry represents a single action (assigned, unassigned, etc.)
 * and includes full context for timeline rendering.
 */
export interface AssignmentHistoryEntry {
  /** Document ID (matches Firestore doc ID) */
  id: string;
  
  /** Company ID for tenant isolation — REQUIRED */
  companyId: string;
  
  /** Well ID this history belongs to */
  wellId: string;
  
  // ─────────────────────────────────────────────────────────────────────────
  // ACTION DETAILS
  // ─────────────────────────────────────────────────────────────────────────
  
  /** What action was performed */
  action: AssignmentAction;
  
  /** IDs of pumpers affected (added, removed, or changed) */
  pumperIds: string[];
  
  /** Names of affected pumpers for display */
  pumperNames: string[];
  
  // ─────────────────────────────────────────────────────────────────────────
  // WHO & HOW
  // ─────────────────────────────────────────────────────────────────────────
  
  /** Manager/user who performed the action */
  performedBy: string;
  performedByName: string;
  
  /** How the assignment was made */
  method: HistoryMethod;
  
  /** Route ID if this was a route-based assignment */
  routeId?: string;
  routeName?: string;
  
  /** Optional reason provided by manager (shown in notifications) */
  reason?: string;
  
  // ─────────────────────────────────────────────────────────────────────────
  // TIMESTAMPS
  // ─────────────────────────────────────────────────────────────────────────
  
  /** When the action occurred (display timestamp) */
  timestamp: FirestoreTimestamp;
  
  /** When this record was created — REQUIRED */
  createdAt: FirestoreTimestamp;
  
  /** Who created this record — REQUIRED */
  createdBy: string;
  
  // ─────────────────────────────────────────────────────────────────────────
  // ADDITIONAL CONTEXT
  // ─────────────────────────────────────────────────────────────────────────
  
  /** Extra metadata for temporary/coverage-related entries */
  metadata?: AssignmentHistoryMetadata;
}

// ─────────────────────────────────────────────────────────────────────────────
// ASSIGNMENT HISTORY HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generate a human-readable timeline sentence for a history entry.
 * 
 * @example
 * // "John Smith was assigned to this well by Manager Jane via Manual"
 * // "Mike Johnson was unassigned from this well by Manager Jane (Temporary Coverage ended)"
 */
export function formatHistoryEntrySentence(entry: AssignmentHistoryEntry): string {
  const pumperNames = entry.pumperNames.join(', ') || 'Unknown';
  const actionLabel = ASSIGNMENT_ACTION_PAST_LABELS[entry.action];
  const methodLabel = HISTORY_METHOD_LABELS[entry.method];
  
  let sentence = `${pumperNames} ${actionLabel} this well by ${entry.performedByName}`;
  
  // Add method context
  if (entry.method !== 'manual') {
    sentence += ` via ${methodLabel}`;
  }
  
  // Add coverage context if applicable
  if (entry.metadata?.temporaryAssignment && entry.metadata.coveringForName) {
    sentence += ` (covering for ${entry.metadata.coveringForName})`;
  }
  
  // Add reason if provided
  if (entry.reason) {
    sentence += ` — "${entry.reason}"`;
  }
  
  return sentence;
}

/**
 * Get the display color set for a history entry based on action
 */
export function getHistoryEntryColors(entry: AssignmentHistoryEntry) {
  return ASSIGNMENT_ACTION_COLORS[entry.action];
}

/**
 * Get the icon name for a history entry based on action
 */
export function getHistoryEntryIcon(entry: AssignmentHistoryEntry): string {
  return ASSIGNMENT_ACTION_COLORS[entry.action].icon;
}

/**
 * Check if a history entry is related to temporary coverage
 */
export function isCoverageRelatedEntry(entry: AssignmentHistoryEntry): boolean {
  return entry.metadata?.temporaryAssignment === true || 
         entry.method === 'temporary' || 
         entry.method === 'coverage_end';
}

/**
 * Check if a history entry is related to backup system
 */
export function isBackupRelatedEntry(entry: AssignmentHistoryEntry): boolean {
  return entry.action === 'backup_activated' || 
         entry.action === 'backup_deactivated' ||
         entry.method === 'backup';
}

/**
 * Group history entries by date for timeline rendering
 */
export function groupHistoryByDate(
  entries: AssignmentHistoryEntry[]
): Map<string, AssignmentHistoryEntry[]> {
  const grouped = new Map<string, AssignmentHistoryEntry[]>();
  
  for (const entry of entries) {
    const timestamp = entry.timestamp instanceof Date 
      ? entry.timestamp 
      : new Date(entry.timestamp.seconds * 1000);
    
    const dateKey = timestamp.toISOString().split('T')[0]; // YYYY-MM-DD
    
    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, []);
    }
    grouped.get(dateKey)!.push(entry);
  }
  
  return grouped;
}

/**
 * Default values for creating a new AssignmentHistoryEntry
 */
export const DEFAULT_ASSIGNMENT_HISTORY_ENTRY: Pick<
  AssignmentHistoryEntry,
  'pumperIds' | 'pumperNames'
> = {
  pumperIds: [],
  pumperNames: [],
};

/**
 * Fields required when creating a new AssignmentHistoryEntry
 */
export type CreateAssignmentHistoryInput = Omit<
  AssignmentHistoryEntry,
  'id'
>;

/**
 * Summary of assignment history for quick display
 */
export interface AssignmentHistorySummary {
  /** Total number of history entries */
  totalEntries: number;
  
  /** Most recent action */
  lastAction?: AssignmentAction;
  lastActionDate?: Date;
  lastActionBy?: string;
  
  /** Counts by action type */
  assignmentCount: number;
  unassignmentCount: number;
  backupActivationCount: number;
  
  /** Coverage history */
  hasBeenInCoverage: boolean;
  coverageCount: number;
}

/**
 * Visibility mode for wells in the pumper app.
 * Controls what wells pumpers can see when logged in.
 * 
 * - all: Pumpers see all company wells (legacy behavior)
 * - assigned: Pumpers only see wells assigned to them
 */
export type WellVisibilityMode = 'all' | 'assigned';

/**
 * Enum values for WellVisibilityMode (for iteration/validation)
 */
export const WELL_VISIBILITY_MODES: readonly WellVisibilityMode[] = [
  'all',
  'assigned',
] as const;

/**
 * Human-readable labels for visibility modes
 */
export const WELL_VISIBILITY_MODE_LABELS: Record<WellVisibilityMode, string> = {
  all: 'All Wells Visible',
  assigned: 'Assigned Wells Only',
};

/**
 * Descriptions for visibility modes (for settings UI help text)
 */
export const WELL_VISIBILITY_MODE_DESCRIPTIONS: Record<WellVisibilityMode, string> = {
  all: 'Pumpers can see and access all company wells. Good for small teams or cross-training.',
  assigned: 'Pumpers only see wells assigned to them. Recommended for larger operations with dedicated routes.',
};

/**
 * Icons for visibility modes (lucide-react icon names)
 */
export const WELL_VISIBILITY_MODE_ICONS: Record<WellVisibilityMode, string> = {
  all: 'Eye',
  assigned: 'EyeOff',
};

/**
 * Company-wide settings for well visibility and assignment notifications.
 * 
 * Path: companies/{companyId}/settings/wellVisibility
 * 
 * This is a singleton document — one per company.
 * Controls company-wide behavior for the assignment system.
 */
export interface WellVisibilitySettings {
  /** 
   * Visibility mode for pumpers.
   * - 'all': Pumpers see all company wells
   * - 'assigned': Pumpers only see their assigned wells
   */
  mode: WellVisibilityMode;
  
  /** 
   * Whether to notify pumpers when they are unassigned from a well.
   * If false, unassignments happen silently (wells just disappear from their list).
   */
  notifyOnUnassign: boolean;
  
  /** 
   * Whether to send notifications when temporary coverage is ending.
   * Triggers 1-day advance warning to both covering and original pumper.
   */
  notifyOnCoverageEnd: boolean;
  
  /** Company ID for tenant isolation — REQUIRED */
  companyId: string;
  
  // ─────────────────────────────────────────────────────────────────────────
  // AUDIT FIELDS
  // ─────────────────────────────────────────────────────────────────────────
  
  /** When settings were last updated */
  updatedAt: FirestoreTimestamp;
  
  /** Who last updated these settings */
  updatedBy: string;
  
  /** When created — REQUIRED */
  createdAt: FirestoreTimestamp;
  
  /** Who created — REQUIRED */
  createdBy: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// WELL VISIBILITY HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Default settings for a new company.
 * Conservative defaults: assigned-only mode with all notifications enabled.
 */
export const DEFAULT_WELL_VISIBILITY_SETTINGS: Pick<
  WellVisibilitySettings,
  'mode' | 'notifyOnUnassign' | 'notifyOnCoverageEnd'
> = {
  mode: 'assigned',           // Safer default — pumpers see only their wells
  notifyOnUnassign: true,     // Keep pumpers informed
  notifyOnCoverageEnd: true,  // Important for coverage handoffs
};

/**
 * Check if a user (pumper) should see all wells based on settings and role
 * Managers always see all wells regardless of settings
 */
export function shouldSeeAllWells(
  settings: WellVisibilitySettings,
  userRole: string
): boolean {
  // Managers/admins always see all wells
  if (userRole === 'admin' || userRole === 'manager' || userRole === 'superadmin') {
    return true;
  }
  
  // Pumpers follow the company setting
  return settings.mode === 'all';
}

/**
 * Check if unassignment notifications should be sent
 */
export function shouldNotifyOnUnassign(settings: WellVisibilitySettings): boolean {
  return settings.notifyOnUnassign;
}

/**
 * Check if coverage end notifications should be sent
 */
export function shouldNotifyOnCoverageEnd(settings: WellVisibilitySettings): boolean {
  return settings.notifyOnCoverageEnd;
}

/**
 * Type for updating visibility settings (excludes audit fields that are auto-set)
 */
export type UpdateWellVisibilityInput = Partial<
  Pick<WellVisibilitySettings, 'mode' | 'notifyOnUnassign' | 'notifyOnCoverageEnd'>
>;

/**
 * Settings UI state helper — describes what each toggle does
 */
export const WELL_VISIBILITY_SETTING_INFO: Record<
  keyof Pick<WellVisibilitySettings, 'mode' | 'notifyOnUnassign' | 'notifyOnCoverageEnd'>,
  {
    label: string;
    description: string;
    icon: string;
  }
> = {
  mode: {
    label: 'Well Visibility Mode',
    description: 'Controls which wells pumpers can see when they log in',
    icon: 'Eye',
  },
  notifyOnUnassign: {
    label: 'Notify on Unassignment',
    description: 'Send notification to pumpers when they are removed from a well',
    icon: 'BellOff',
  },
  notifyOnCoverageEnd: {
    label: 'Coverage End Reminders',
    description: 'Send 1-day advance warning when temporary coverage is ending',
    icon: 'Bell',
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// PUMPER WORKLOAD
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Suggested action types for workload rebalancing.
 * Used in automated recommendations to managers.
 */
export type WorkloadChangeAction = 'add_wells' | 'remove_wells' | 'rebalance';

/**
 * All possible workload change actions (for iteration)
 */
export const WORKLOAD_CHANGE_ACTIONS: readonly WorkloadChangeAction[] = [
  'add_wells',
  'remove_wells',
  'rebalance',
] as const;

/**
 * Human-readable labels for workload change actions
 */
export const WORKLOAD_CHANGE_ACTION_LABELS: Record<WorkloadChangeAction, string> = {
  add_wells: 'Assign More Wells',
  remove_wells: 'Remove Some Wells',
  rebalance: 'Rebalance Route',
};

/**
 * Descriptions for workload change actions (for UI tooltips)
 */
export const WORKLOAD_CHANGE_ACTION_DESCRIPTIONS: Record<WorkloadChangeAction, string> = {
  add_wells: 'This pumper has capacity and could handle additional wells',
  remove_wells: 'This pumper is overloaded and should have wells reassigned',
  rebalance: 'Wells should be shuffled between pumpers to optimize routes',
};

/**
 * Icons for workload change actions (lucide-react)
 */
export const WORKLOAD_CHANGE_ACTION_ICONS: Record<WorkloadChangeAction, string> = {
  add_wells: 'Plus',
  remove_wells: 'Minus',
  rebalance: 'ArrowLeftRight',
};

/**
 * Suggested change for pumper workload rebalancing.
 * Generated by workload analytics when imbalances detected.
 */
export interface WorkloadSuggestedChange {
  /** What action is recommended */
  action: WorkloadChangeAction;
  
  /** How many wells to add/remove (not used for rebalance) */
  wellCount: number;
  
  /** Target pumper for 'rebalance' action (who to transfer to/from) */
  targetPumperId?: string;
  
  /** Target pumper name (for display without extra lookup) */
  targetPumperName?: string;
  
  /** Why this change is recommended */
  reason?: string;
}

/**
 * Pumper workload analytics data.
 * 
 * This is NOT stored in Firestore — it's computed on-demand from:
 * - Well assignments (companies/{companyId}/wells)
 * - Route optimization data
 * - Temporary assignments
 * 
 * Used by: Manager dashboard, assignment modal, workload reports
 */
export interface PumperWorkload {
  // ─────────────────────────────────────────────────────────────────────────
  // IDENTITY
  // ─────────────────────────────────────────────────────────────────────────
  
  /** Pumper's user ID */
  pumperId: string;
  
  /** Pumper's display name (for UI) */
  pumperName: string;
  
  // ─────────────────────────────────────────────────────────────────────────
  // RAW COUNTS
  // ─────────────────────────────────────────────────────────────────────────
  
  /** Total wells assigned to this pumper (primary + backup) */
  wellCount: number;
  
  /** Active wells only (excludes shut_in, plugged, inactive) */
  activeWellCount: number;
  
  /** Wells where this pumper is providing temporary coverage */
  temporaryCoverageCount: number;
  
  // ─────────────────────────────────────────────────────────────────────────
  // WEIGHTED FACTORS (for score calculation)
  // ─────────────────────────────────────────────────────────────────────────
  
  /** 
   * Total daily driving miles based on optimized route.
   * Higher mileage = more time spent traveling = less capacity.
   */
  totalDailyMiles: number;
  
  /**
   * Average check frequency across all assigned wells.
   * Higher frequency = more visits required = heavier workload.
   * Scale: 1 (weekly) to 7 (daily)
   */
  avgCheckFrequency: number;
  
  /**
   * Count of high-priority wells (priority 1-3).
   * High-priority wells require more attention and response time.
   */
  highPriorityWellCount: number;
  
  // ─────────────────────────────────────────────────────────────────────────
  // COMPUTED SCORE
  // ─────────────────────────────────────────────────────────────────────────
  
  /**
   * Workload score from 0-100.
   * Calculated as:
   *   wellCountScore   = min(wellCount × 2, 40)
   *   distanceScore    = min(dailyMiles ÷ 10, 25)
   *   frequencyScore   = min(avgCheckFrequency × 5, 20)
   *   priorityScore    = min(highPriorityWellCount × 3, 15)
   *   TOTAL = min(sum, 100)
   */
  workloadScore: number;
  
  /** Status derived from workloadScore */
  workloadStatus: WorkloadStatus;
  
  // ─────────────────────────────────────────────────────────────────────────
  // RECOMMENDATIONS
  // ─────────────────────────────────────────────────────────────────────────
  
  /** Optional suggested changes to improve workload balance */
  suggestedChanges?: WorkloadSuggestedChange;
}

// ─────────────────────────────────────────────────────────────────────────────
// PUMPER WORKLOAD HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Score weights for workload calculation.
 * Exported so UI can show breakdown to users.
 */
export const WORKLOAD_SCORE_WEIGHTS = {
  wellCount: {
    multiplier: 2,
    maxScore: 40,
    label: 'Well Count',
    description: 'Number of wells assigned (×2, max 40 points)',
  },
  distance: {
    multiplier: 0.1,  // divide by 10
    maxScore: 25,
    label: 'Daily Distance',
    description: 'Miles driven daily (÷10, max 25 points)',
  },
  frequency: {
    multiplier: 5,
    maxScore: 20,
    label: 'Check Frequency',
    description: 'Average visits per week (×5, max 20 points)',
  },
  priority: {
    multiplier: 3,
    maxScore: 15,
    label: 'High Priority Wells',
    description: 'Wells with priority 1-3 (×3, max 15 points)',
  },
} as const;

/**
 * Calculate workload score from raw values.
 * Uses the formula defined in WELL_ASSIGNMENT_FEATURE.md
 * 
 * @example
 * const score = calculateWorkloadScore(15, 80, 2.5, 3);
 * // 30 + 8 + 12.5 + 9 = 59.5 → 60
 */
export function calculateWorkloadScore(
  wellCount: number,
  totalDailyMiles: number,
  avgCheckFrequency: number,
  highPriorityWellCount: number
): number {
  const wellCountScore = Math.min(wellCount * 2, 40);
  const distanceScore = Math.min(totalDailyMiles / 10, 25);
  const frequencyScore = Math.min(avgCheckFrequency * 5, 20);
  const priorityScore = Math.min(highPriorityWellCount * 3, 15);
  
  const total = wellCountScore + distanceScore + frequencyScore + priorityScore;
  return Math.min(Math.round(total), 100);
}

/**
 * Get workload status from score.
 */
export function getWorkloadStatusFromScore(score: number): WorkloadStatus {
  if (score <= 30) return 'light';
  if (score <= 60) return 'balanced';
  if (score <= 80) return 'heavy';
  return 'overloaded';
}

/**
 * Calculate full workload for a pumper from raw inputs.
 * This is the main workload calculation function.
 */
export function calculatePumperWorkload(
  pumperId: string,
  pumperName: string,
  wellCount: number,
  activeWellCount: number,
  temporaryCoverageCount: number,
  totalDailyMiles: number,
  avgCheckFrequency: number,
  highPriorityWellCount: number
): PumperWorkload {
  const workloadScore = calculateWorkloadScore(
    wellCount,
    totalDailyMiles,
    avgCheckFrequency,
    highPriorityWellCount
  );
  
  const workloadStatus = getWorkloadStatusFromScore(workloadScore);
  
  // Generate suggestions based on status
  let suggestedChanges: WorkloadSuggestedChange | undefined;
  
  if (workloadStatus === 'light' && wellCount < 5) {
    suggestedChanges = {
      action: 'add_wells',
      wellCount: Math.min(10 - wellCount, 5), // Suggest adding up to 5 wells
      reason: 'This pumper has light workload and capacity for more wells',
    };
  } else if (workloadStatus === 'overloaded') {
    suggestedChanges = {
      action: 'remove_wells',
      wellCount: Math.ceil(wellCount * 0.2), // Suggest removing ~20%
      reason: 'This pumper is overloaded and at risk of burnout',
    };
  } else if (workloadStatus === 'heavy' && temporaryCoverageCount > 2) {
    suggestedChanges = {
      action: 'rebalance',
      wellCount: temporaryCoverageCount,
      reason: 'Heavy workload driven by temporary coverage - consider permanent reassignment',
    };
  }
  
  return {
    pumperId,
    pumperName,
    wellCount,
    activeWellCount,
    temporaryCoverageCount,
    totalDailyMiles,
    avgCheckFrequency,
    highPriorityWellCount,
    workloadScore,
    workloadStatus,
    suggestedChanges,
  };
}

/**
 * Type guard for PumperWorkload
 */
export function isPumperWorkload(obj: unknown): obj is PumperWorkload {
  if (!obj || typeof obj !== 'object') return false;
  const w = obj as Partial<PumperWorkload>;
  return (
    typeof w.pumperId === 'string' &&
    typeof w.pumperName === 'string' &&
    typeof w.wellCount === 'number' &&
    typeof w.activeWellCount === 'number' &&
    typeof w.temporaryCoverageCount === 'number' &&
    typeof w.totalDailyMiles === 'number' &&
    typeof w.avgCheckFrequency === 'number' &&
    typeof w.highPriorityWellCount === 'number' &&
    typeof w.workloadScore === 'number' &&
    WORKLOAD_STATUSES.includes(w.workloadStatus as WorkloadStatus)
  );
}

/**
 * Default empty workload for a new pumper (no wells assigned)
 */
export function createEmptyWorkload(pumperId: string, pumperName: string): PumperWorkload {
  return {
    pumperId,
    pumperName,
    wellCount: 0,
    activeWellCount: 0,
    temporaryCoverageCount: 0,
    totalDailyMiles: 0,
    avgCheckFrequency: 0,
    highPriorityWellCount: 0,
    workloadScore: 0,
    workloadStatus: 'light',
    suggestedChanges: {
      action: 'add_wells',
      wellCount: 5,
      reason: 'No wells assigned - assign wells to this pumper',
    },
  };
}

/**
 * Get workload score breakdown for display in UI.
 * Shows contribution of each factor to total score.
 */
export function getWorkloadScoreBreakdown(workload: PumperWorkload): {
  wellCountScore: number;
  distanceScore: number;
  frequencyScore: number;
  priorityScore: number;
  total: number;
} {
  const wellCountScore = Math.min(workload.wellCount * 2, 40);
  const distanceScore = Math.min(workload.totalDailyMiles / 10, 25);
  const frequencyScore = Math.min(workload.avgCheckFrequency * 5, 20);
  const priorityScore = Math.min(workload.highPriorityWellCount * 3, 15);
  
  return {
    wellCountScore: Math.round(wellCountScore * 10) / 10,
    distanceScore: Math.round(distanceScore * 10) / 10,
    frequencyScore: Math.round(frequencyScore * 10) / 10,
    priorityScore: Math.round(priorityScore * 10) / 10,
    total: workload.workloadScore,
  };
}

/**
 * Compare two pumpers for workload balancing.
 * Returns positive if a > b (a is more overloaded)
 */
export function compareWorkloads(a: PumperWorkload, b: PumperWorkload): number {
  return a.workloadScore - b.workloadScore;
}

/**
 * Sort pumpers by workload (lightest first for assignment suggestions)
 */
export function sortByLightestWorkload(workloads: PumperWorkload[]): PumperWorkload[] {
  return [...workloads].sort(compareWorkloads);
}

/**
 * Sort pumpers by workload (heaviest first for rebalancing review)
 */
export function sortByHeaviestWorkload(workloads: PumperWorkload[]): PumperWorkload[] {
  return [...workloads].sort((a, b) => compareWorkloads(b, a));
}

/**
 * Get pumpers who could take on more work
 */
export function getPumpersWithCapacity(workloads: PumperWorkload[]): PumperWorkload[] {
  return workloads.filter(
    w => w.workloadStatus === 'light' || w.workloadStatus === 'balanced'
  );
}

/**
 * Get overloaded pumpers who need help
 */
export function getOverloadedPumpers(workloads: PumperWorkload[]): PumperWorkload[] {
  return workloads.filter(
    w => w.workloadStatus === 'heavy' || w.workloadStatus === 'overloaded'
  );
}

/**
 * Calculate team average workload score
 */
export function getTeamAverageWorkload(workloads: PumperWorkload[]): number {
  if (workloads.length === 0) return 0;
  const total = workloads.reduce((sum, w) => sum + w.workloadScore, 0);
  return Math.round(total / workloads.length);
}

/**
 * Workload summary for team dashboard
 */
export interface TeamWorkloadSummary {
  totalPumpers: number;
  totalWells: number;
  avgWorkloadScore: number;
  lightCount: number;
  balancedCount: number;
  heavyCount: number;
  overloadedCount: number;
  wellBalanceIndex: number; // 0-100, higher = better distribution
}

/**
 * Calculate team workload summary
 */
export function getTeamWorkloadSummary(workloads: PumperWorkload[]): TeamWorkloadSummary {
  const lightCount = workloads.filter(w => w.workloadStatus === 'light').length;
  const balancedCount = workloads.filter(w => w.workloadStatus === 'balanced').length;
  const heavyCount = workloads.filter(w => w.workloadStatus === 'heavy').length;
  const overloadedCount = workloads.filter(w => w.workloadStatus === 'overloaded').length;
  
  const totalWells = workloads.reduce((sum, w) => sum + w.wellCount, 0);
  const avgWorkloadScore = getTeamAverageWorkload(workloads);
  
  // Calculate balance index: penalize variance from mean
  const scores = workloads.map(w => w.workloadScore);
  const variance = scores.length > 1
    ? scores.reduce((sum, s) => sum + Math.pow(s - avgWorkloadScore, 2), 0) / scores.length
    : 0;
  const stdDev = Math.sqrt(variance);
  const wellBalanceIndex = Math.max(0, Math.round(100 - stdDev * 2));
  
  return {
    totalPumpers: workloads.length,
    totalWells,
    avgWorkloadScore,
    lightCount,
    balancedCount,
    heavyCount,
    overloadedCount,
    wellBalanceIndex,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// WELL ASSIGNMENT FIELDS (Extension interface for Well type)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Well assignment fields to be merged into the Well interface.
 * 
 * This interface defines all assignment-related fields that extend the base Well type.
 * It supports:
 * - Multi-pumper assignment (assignedPumperIds array)
 * - Backward compatibility (assignedPumperId single field)
 * - Primary/Backup pumper system
 * - Temporary coverage assignments
 * 
 * Usage: Extend your Well interface with this or use intersection type
 * ```typescript
 * type WellWithAssignments = Well & WellAssignmentFields;
 * ```
 */
export interface WellAssignmentFields {
  // ─────────────────────────────────────────────────────────────────────────
  // ASSIGNMENT ARRAY (NEW - supports multiple pumpers)
  // ─────────────────────────────────────────────────────────────────────────
  
  /** 
   * Array of pumper IDs assigned to this well.
   * - First element is always the primary pumper
   * - Supports multi-pumper assignment for high-frequency wells
   * - REQUIRED field (use empty array [] if unassigned)
   */
  assignedPumperIds: string[];
  
  // ─────────────────────────────────────────────────────────────────────────
  // BACKWARD COMPATIBILITY (will be deprecated)
  // ─────────────────────────────────────────────────────────────────────────
  
  /**
   * Legacy single pumper ID field.
   * @deprecated Use assignedPumperIds[0] instead
   * 
   * IMPORTANT: Keep in sync with assignedPumperIds[0]
   * This field exists for backward compatibility with:
   * - Existing queries
   * - Mobile app versions < 2.0
   * - Legacy reports
   */
  assignedPumperId?: string;
  
  // ─────────────────────────────────────────────────────────────────────────
  // PRIMARY/BACKUP SYSTEM
  // ─────────────────────────────────────────────────────────────────────────
  
  /** Primary pumper responsible for this well */
  primaryPumperId?: string;
  
  /** Primary pumper display name (denormalized for UI performance) */
  primaryPumperName?: string;
  
  /** Backup pumper who covers when primary is unavailable */
  backupPumperId?: string;
  
  /** Backup pumper display name (denormalized for UI performance) */
  backupPumperName?: string;
  
  /** Whether backup is currently active (covering for primary) */
  backupActive: boolean;
  
  /** When backup was activated */
  backupActivatedAt?: FirestoreTimestamp;
  
  /** Who activated the backup (manager ID or 'system' for auto-activation) */
  backupActivatedBy?: string;
  
  // ─────────────────────────────────────────────────────────────────────────
  // TEMPORARY COVERAGE
  // ─────────────────────────────────────────────────────────────────────────
  
  /**
   * Temporary coverage assignment details.
   * Present only when someone is temporarily covering this well.
   * Uses the embedded TemporaryAssignment interface.
   */
  temporaryAssignment?: TemporaryAssignment;
}

// ─────────────────────────────────────────────────────────────────────────────
// WELL ASSIGNMENT FIELD HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Default assignment fields for a new well (unassigned state)
 */
export const DEFAULT_WELL_ASSIGNMENT_FIELDS: WellAssignmentFields = {
  assignedPumperIds: [],
  assignedPumperId: undefined,
  primaryPumperId: undefined,
  primaryPumperName: undefined,
  backupPumperId: undefined,
  backupPumperName: undefined,
  backupActive: false,
  backupActivatedAt: undefined,
  backupActivatedBy: undefined,
  temporaryAssignment: undefined,
};

/**
 * Check if a well has any pumper assigned
 */
export function wellHasAssignment(well: WellAssignmentFields): boolean {
  return well.assignedPumperIds.length > 0 || !!well.assignedPumperId;
}

/**
 * Check if a well is an orphan (no pumper assigned)
 */
export function isOrphanWell(well: WellAssignmentFields): boolean {
  return !wellHasAssignment(well);
}

/**
 * Get the effective primary pumper ID (handles legacy field)
 */
export function getEffectivePrimaryPumperId(well: WellAssignmentFields): string | undefined {
  return well.primaryPumperId || well.assignedPumperIds[0] || well.assignedPumperId;
}

/**
 * Check if backup is currently covering
 */
export function isBackupActive(well: WellAssignmentFields): boolean {
  return well.backupActive && !!well.backupPumperId;
}

/**
 * Check if well has temporary coverage active
 */
export function hasActiveTemporaryCoverage(well: WellAssignmentFields): boolean {
  return !!well.temporaryAssignment?.active;
}

/**
 * Get the currently responsible pumper ID (considering backup and coverage)
 */
export function getCurrentResponsiblePumperId(well: WellAssignmentFields): string | undefined {
  // Temporary coverage takes highest priority
  if (well.temporaryAssignment?.active) {
    return well.temporaryAssignment.coveringPumperId;
  }
  
  // Backup active takes second priority
  if (well.backupActive && well.backupPumperId) {
    return well.backupPumperId;
  }
  
  // Otherwise, primary pumper
  return getEffectivePrimaryPumperId(well);
}

/**
 * Get the currently responsible pumper name (considering backup and coverage)
 */
export function getCurrentResponsiblePumperName(well: WellAssignmentFields): string | undefined {
  // Temporary coverage takes highest priority
  if (well.temporaryAssignment?.active) {
    return well.temporaryAssignment.coveringPumperName;
  }
  
  // Backup active takes second priority
  if (well.backupActive && well.backupPumperName) {
    return well.backupPumperName;
  }
  
  // Otherwise, primary pumper name
  return well.primaryPumperName;
}

/**
 * Check if a specific pumper is assigned to this well (any capacity)
 */
export function isPumperAssigned(well: WellAssignmentFields, pumperId: string): boolean {
  return (
    well.assignedPumperIds.includes(pumperId) ||
    well.assignedPumperId === pumperId ||
    well.primaryPumperId === pumperId ||
    well.backupPumperId === pumperId ||
    well.temporaryAssignment?.coveringPumperId === pumperId
  );
}

/**
 * Get assignment role for a pumper on this well
 */
export type PumperAssignmentRole = 
  | 'primary'
  | 'backup'
  | 'temporary_coverage'
  | 'secondary'  // In assignedPumperIds but not primary
  | 'none';

/**
 * Get the role a pumper has on a specific well
 */
export function getPumperRoleOnWell(
  well: WellAssignmentFields, 
  pumperId: string
): PumperAssignmentRole {
  // Check temporary coverage first (highest priority role)
  if (well.temporaryAssignment?.active && 
      well.temporaryAssignment.coveringPumperId === pumperId) {
    return 'temporary_coverage';
  }
  
  // Check primary
  if (well.primaryPumperId === pumperId || 
      (!well.primaryPumperId && well.assignedPumperIds[0] === pumperId) ||
      (!well.primaryPumperId && well.assignedPumperId === pumperId)) {
    return 'primary';
  }
  
  // Check backup
  if (well.backupPumperId === pumperId) {
    return 'backup';
  }
  
  // Check secondary (in array but not primary)
  if (well.assignedPumperIds.includes(pumperId)) {
    return 'secondary';
  }
  
  return 'none';
}

/**
 * Labels for pumper assignment roles
 */
export const PUMPER_ASSIGNMENT_ROLE_LABELS: Record<PumperAssignmentRole, string> = {
  primary: 'Primary',
  backup: 'Backup',
  temporary_coverage: 'Covering',
  secondary: 'Secondary',
  none: 'Not Assigned',
};

/**
 * Colors for pumper assignment roles (Tailwind)
 */
export const PUMPER_ASSIGNMENT_ROLE_COLORS: Record<PumperAssignmentRole, {
  bg: string;
  text: string;
  border: string;
  icon: string;
}> = {
  primary: {
    bg: 'bg-blue-100',
    text: 'text-blue-700',
    border: 'border-blue-300',
    icon: 'User',
  },
  backup: {
    bg: 'bg-purple-100',
    text: 'text-purple-700',
    border: 'border-purple-300',
    icon: 'UserPlus',
  },
  temporary_coverage: {
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    border: 'border-amber-300',
    icon: 'Clock',
  },
  secondary: {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    border: 'border-gray-300',
    icon: 'Users',
  },
  none: {
    bg: 'bg-red-50',
    text: 'text-red-600',
    border: 'border-red-200',
    icon: 'UserX',
  },
};

/**
 * Create assignment fields for assigning a single pumper (simple case)
 */
export function createSimpleAssignment(
  pumperId: string,
  pumperName: string
): Partial<WellAssignmentFields> {
  return {
    assignedPumperIds: [pumperId],
    assignedPumperId: pumperId, // Backward compat
    primaryPumperId: pumperId,
    primaryPumperName: pumperName,
    backupPumperId: undefined,
    backupPumperName: undefined,
    backupActive: false,
    temporaryAssignment: undefined,
  };
}

/**
 * Create assignment fields for primary + backup (recommended setup)
 */
export function createPrimaryBackupAssignment(
  primaryId: string,
  primaryName: string,
  backupId: string,
  backupName: string
): Partial<WellAssignmentFields> {
  return {
    assignedPumperIds: [primaryId, backupId],
    assignedPumperId: primaryId, // Backward compat
    primaryPumperId: primaryId,
    primaryPumperName: primaryName,
    backupPumperId: backupId,
    backupPumperName: backupName,
    backupActive: false,
    temporaryAssignment: undefined,
  };
}

/**
 * Create fields to clear all assignments (orphan the well)
 */
export function createUnassignmentFields(): WellAssignmentFields {
  return { ...DEFAULT_WELL_ASSIGNMENT_FIELDS };
}

/**
 * Type guard for WellAssignmentFields
 */
export function hasWellAssignmentFields(obj: unknown): obj is WellAssignmentFields {
  if (!obj || typeof obj !== 'object') return false;
  const w = obj as Partial<WellAssignmentFields>;
  return (
    Array.isArray(w.assignedPumperIds) &&
    typeof w.backupActive === 'boolean'
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ASSIGNMENT NOTIFICATION TYPES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Notification types specific to well assignments.
 * These extend the base notification system for assignment events.
 */
export type AssignmentNotificationType =
  | 'well_assigned'          // Pumper assigned to a new well
  | 'well_unassigned'        // Pumper removed from a well
  | 'bulk_wells_assigned'    // Multiple wells assigned at once
  | 'coverage_started'       // Temporary coverage began
  | 'coverage_ending'        // Coverage ending soon (1-day warning)
  | 'coverage_ended'         // Coverage period completed
  | 'coverage_reminder'      // Reminder during active coverage
  | 'backup_activated'       // Backup pumper taking over
  | 'backup_deactivated'     // Primary pumper resumed
  | 'orphan_wells_detected'; // Wells without any assignment

/**
 * All assignment notification types (for iteration/validation)
 */
export const ASSIGNMENT_NOTIFICATION_TYPES: readonly AssignmentNotificationType[] = [
  'well_assigned',
  'well_unassigned',
  'bulk_wells_assigned',
  'coverage_started',
  'coverage_ending',
  'coverage_ended',
  'coverage_reminder',
  'backup_activated',
  'backup_deactivated',
  'orphan_wells_detected',
] as const;

/**
 * Human-readable labels for assignment notification types
 */
export const ASSIGNMENT_NOTIFICATION_LABELS: Record<AssignmentNotificationType, string> = {
  well_assigned: 'Well Assigned',
  well_unassigned: 'Well Unassigned',
  bulk_wells_assigned: 'Multiple Wells Assigned',
  coverage_started: 'Coverage Started',
  coverage_ending: 'Coverage Ending Soon',
  coverage_ended: 'Coverage Ended',
  coverage_reminder: 'Coverage Reminder',
  backup_activated: 'Backup Activated',
  backup_deactivated: 'Backup Deactivated',
  orphan_wells_detected: 'Orphan Wells Detected',
};

/**
 * Icons for assignment notification types (lucide-react)
 */
export const ASSIGNMENT_NOTIFICATION_ICONS: Record<AssignmentNotificationType, string> = {
  well_assigned: 'UserPlus',
  well_unassigned: 'UserMinus',
  bulk_wells_assigned: 'Users',
  coverage_started: 'Clock',
  coverage_ending: 'AlertTriangle',
  coverage_ended: 'CheckCircle',
  coverage_reminder: 'Bell',
  backup_activated: 'Shield',
  backup_deactivated: 'ShieldOff',
  orphan_wells_detected: 'AlertOctagon',
};

/**
 * Priority levels for assignment notifications
 * Higher = more urgent
 */
export const ASSIGNMENT_NOTIFICATION_PRIORITY: Record<AssignmentNotificationType, number> = {
  orphan_wells_detected: 5,  // Critical - wells going unchecked
  coverage_ending: 4,        // High - needs action soon
  backup_activated: 3,       // Medium-high - ops continuity
  coverage_started: 3,
  coverage_ended: 2,
  backup_deactivated: 2,
  well_assigned: 2,
  well_unassigned: 2,
  bulk_wells_assigned: 2,
  coverage_reminder: 1,      // Low - informational
};

/**
 * Target audience for each notification type
 */
export type NotificationAudience = 'pumper' | 'manager' | 'both';

export const ASSIGNMENT_NOTIFICATION_AUDIENCE: Record<AssignmentNotificationType, NotificationAudience> = {
  well_assigned: 'pumper',
  well_unassigned: 'pumper',
  bulk_wells_assigned: 'pumper',
  coverage_started: 'both',
  coverage_ending: 'both',
  coverage_ended: 'both',
  coverage_reminder: 'pumper',
  backup_activated: 'both',
  backup_deactivated: 'both',
  orphan_wells_detected: 'manager',
};

/**
 * Check if a notification type is high priority (3+)
 */
export function isHighPriorityNotification(type: AssignmentNotificationType): boolean {
  return ASSIGNMENT_NOTIFICATION_PRIORITY[type] >= 3;
}

/**
 * Check if a notification type should go to managers
 */
export function shouldNotifyManagers(type: AssignmentNotificationType): boolean {
  const audience = ASSIGNMENT_NOTIFICATION_AUDIENCE[type];
  return audience === 'manager' || audience === 'both';
}

/**
 * Check if a notification type should go to pumpers
 */
export function shouldNotifyPumpers(type: AssignmentNotificationType): boolean {
  const audience = ASSIGNMENT_NOTIFICATION_AUDIENCE[type];
  return audience === 'pumper' || audience === 'both';
}

/**
 * Get notification types for coverage events
 */
export function getCoverageNotificationTypes(): AssignmentNotificationType[] {
  return [
    'coverage_started',
    'coverage_ending',
    'coverage_ended',
    'coverage_reminder',
  ];
}

/**
 * Get notification types for backup events
 */
export function getBackupNotificationTypes(): AssignmentNotificationType[] {
  return [
    'backup_activated',
    'backup_deactivated',
  ];
}

/**
 * Type guard for AssignmentNotificationType
 */
export function isAssignmentNotificationType(
  value: unknown
): value is AssignmentNotificationType {
  return (
    typeof value === 'string' &&
    ASSIGNMENT_NOTIFICATION_TYPES.includes(value as AssignmentNotificationType)
  );
}
